import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { RenderUtils } from './RenderUtils';
import {
	MeshStandardMaterial,
	ConeGeometry,
	Vector3,
	Group,
	Mesh,
	Box3,
} from 'three';
import { ProjectRenderer } from './ProjectRenderer';
import { SiphonsManager } from './SiphonsManager';

const LINE_WIDTH = 2;

const material = new LineMaterial({
		color: 0x2e2e2e,
		linewidth: LINE_WIDTH,
	}),
	arrowHeadMat = new MeshStandardMaterial({ color: material.color });

const coneGeometry = new ConeGeometry(0.0025, 0.0125),
	halfConeHeight = coneGeometry.parameters.height / 2;

export class ProductMeasures {
	private _visible = false;

	private lines = getEmptyLines();
	static readonly ARROW_TO_BOX_OFFSET = 0.015;

	constructor(private renderer: ProjectRenderer) {
		this.renderer = renderer;
	}

	get visible() {
		return this._visible;
	}

	set visible(value: boolean) {
		this._visible = value;

		let key: keyof typeof this.lines;
		for (key in this.lines) {
			this.lines[key].group.visible = this.visible;
		}
	}

	private createArrowhead(position: Vector3, direction: Vector3) {
		const cone = new Mesh(coneGeometry, arrowHeadMat);

		const offsetVector = direction
			.clone()
			.normalize()
			.multiplyScalar(halfConeHeight);
		cone.position.copy(position).sub(offsetVector);

		cone.quaternion.setFromUnitVectors(
			new Vector3(0, 1, 0),
			direction.clone().normalize()
		);

		return cone;
	}

	private setLinesData(box: Box3) {
		const { xStart, xEnd } =
			this.renderer.productBuilder.getAvailableSiphonsAxisData();

		this.lines.productLength.length =
			this.renderer.productBuilder.length * 1000;

		this.lines.productLength.points[0].set(
			xStart,
			box.max.y + ProductMeasures.ARROW_TO_BOX_OFFSET,
			box.min.z + box.max.z
		);
		this.lines.productLength.points[1].set(
			xEnd,
			box.max.y + ProductMeasures.ARROW_TO_BOX_OFFSET,
			box.min.z + box.max.z
		);

		this.lines.verticalLeft.points[0].set(
			xStart,
			box.max.y + ProductMeasures.ARROW_TO_BOX_OFFSET * 1.5,
			box.min.z + box.max.z
		);
		this.lines.verticalLeft.points[1].set(
			xStart,
			box.max.y - ProductMeasures.ARROW_TO_BOX_OFFSET * 1.5,
			box.min.z + box.max.z
		);

		this.lines.verticalRight.points[0].set(
			xEnd,
			box.max.y + ProductMeasures.ARROW_TO_BOX_OFFSET * 1.5,
			box.min.z + box.max.z
		);
		this.lines.verticalRight.points[1].set(
			xEnd,
			box.max.y - ProductMeasures.ARROW_TO_BOX_OFFSET * 1.5,
			box.min.z + box.max.z
		);

		const siphons = this.renderer.siphonsManager.getSiphonsArray();
		if (!siphons.length) return;

		const siphon = siphons[0],
			siphonHeight = siphon.box.getSize(new Vector3()).y,
			heightOffset = siphonHeight * (siphons.length + 1);

		this.lines.verticalLeft.points[0].set(
			xStart,
			box.max.y + ProductMeasures.ARROW_TO_BOX_OFFSET * 1.5,
			box.min.z + box.max.z
		);
		this.lines.verticalLeft.points[1].set(
			xStart,
			box.max.y - heightOffset,
			box.min.z + box.max.z
		);

		for (
			let index = 0;
			index < this.renderer.siphonsManager.getSiphonsArray().length;
			index++
		) {
			const siphon = this.renderer.siphonsManager.getSiphonsArray()[index];

			const siphonBox = siphon.box.clone();
			siphonBox.expandByVector(
				new Vector3(SiphonsManager.SIPHONS_MIN_GAP, 0, 0).negate()
			);
			const center = siphonBox.getCenter(new Vector3());

			const siphonHeight = siphonBox.getSize(new Vector3()).y,
				heightOffset = siphonHeight * (index + 2);

			this.lines[`siphon_${siphon.id}_left`] = {
				group: new Group(),
				points: [new Vector3(), new Vector3()],
				withArrow: true,
				length: Math.round(siphon.posX * 1000),
			};

			this.lines[`siphon_${siphon.id}_left`].points[0].set(
				xStart,
				box.min.y + box.max.y * 2 - heightOffset,
				box.min.z + box.max.z
			);
			this.lines[`siphon_${siphon.id}_left`].points[1].set(
				center.x,
				box.min.y + box.max.y * 2 - heightOffset,
				box.min.z + box.max.z
			);

			this.lines[`siphon_${siphon.id}_vertical`] = {
				group: new Group(),
				points: [new Vector3(), new Vector3()],
				withArrow: false,
				length: 0,
			};

			this.lines[`siphon_${siphon.id}_vertical`].points[0].set(
				center.x,
				box.min.y + box.max.y * 2 - heightOffset,
				box.min.z + box.max.z
			);
			this.lines[`siphon_${siphon.id}_vertical`].points[1].set(
				center.x,
				box.min.y,
				box.min.z + box.max.z
			);
		}
	}

	private drawArrowLine(key: keyof typeof this.lines) {
		const lineSettings = this.lines[key];

		const start = lineSettings.points[1],
			end = lineSettings.points[0];
		const direction = end.clone().sub(start);

		const shortenedLine = [
			start.clone().add(direction.clone().multiplyScalar(halfConeHeight / 3)),
			end.clone().sub(direction.clone().multiplyScalar(halfConeHeight / 3)),
		];

		const geo = new LineGeometry();

		const line = new Line2(geo.setFromPoints(shortenedLine), material);
		this.renderer.scene.add(line);

		if (lineSettings.withArrow) {
			const startArrow = this.createArrowhead(
				start,
				direction.clone().negate()
			);
			const endArrow = this.createArrowhead(end, direction);
			lineSettings.group.add(line, startArrow, endArrow);
			this.createLabel(key);
		} else lineSettings.group.add(line);

		lineSettings.group.layers.enableAll();
		this.renderer.scene.add(lineSettings.group);
	}

	private createLabel(key: keyof typeof this.lines) {
		const lineSettings = this.lines[key];

		const start = lineSettings.points[1],
			end = lineSettings.points[0];

		const center = start.clone().add(end).divideScalar(2);

		const div = document.createElement('div');
		div.className = 'renderer-label';

		const lengthLabel = new CSS2DObject(div);
		lengthLabel.position.copy(center);

		switch (key) {
			case 'productLength':
				lengthLabel.position.y += ProductMeasures.ARROW_TO_BOX_OFFSET;
				break;
			default:
				lengthLabel.position.y += ProductMeasures.ARROW_TO_BOX_OFFSET;
		}

		div.textContent = `${Math.round(lineSettings.length)} mm`;

		lineSettings.group.add(lengthLabel);
		lengthLabel.layers.set(0);
	}

	draw() {
		this.renderer.product.updateMatrixWorld(true);
		const box = new Box3().setFromObject(this.renderer.product);

		this.dispose();
		this.setLinesData(box);

		for (const key in this.lines) {
			this.drawArrowLine(key);
		}

		this.visible = this.visible;
	}

	toggleVisibility() {
		this.visible = !this.visible;
	}

	dispose() {
		let key: keyof typeof this.lines;
		for (key in this.lines) {
			RenderUtils.clearGroup(this.lines[key].group);
			this.lines[key].group.removeFromParent();
		}
		this.lines = getEmptyLines();
	}
}

function getEmptyLines(): Record<
	string,
	{ group: Group; points: Vector3[]; withArrow: boolean; length: number }
> {
	return {
		productLength: {
			group: new Group(),
			points: [new Vector3(), new Vector3()],
			withArrow: true,
			length: 0,
		},
		verticalLeft: {
			group: new Group(),
			points: [new Vector3(), new Vector3()],
			withArrow: false,
			length: 0,
		},
		verticalRight: {
			group: new Group(),
			points: [new Vector3(), new Vector3()],
			withArrow: false,
			length: 0,
		},
	};
}
